// BannerDisplay.jsx
import React from 'react';
import { useBanners } from '../../hooks/useBanners';
import loadingImage from '../../assets/images/1002.jpg';
import { getImageUrl } from '../../utils/getImageUrl';

const BannerDisplay = ({
  location,
  section = null,
  userType = 'all',
  className = '',
  showLoadingImage = true, // Changed from showLoadingSpinner
  maxBanners = null,
  layout = 'default', // 'default', 'slider', 'grid', 'stack'
  height = '400px',
  width = '100%', // Changed default to 100%
  aspectRatio = null, // New prop for responsive aspect ratio
  minHeight = '200px', // New prop for minimum height
  maxHeight = '600px',  // New prop for maximum height
  imageFit = 'cover' // New prop: 'cover', 'contain', 'fill', 'scale-down', 'none'
}) => {
  const { banners, loading, error, handleBannerClick } = useBanners(
    location,
    section,
    userType,
    {
      autoTrackImpressions: true,
      onBannerClick: null
    }
  );

  const displayBanners = maxBanners ? banners.slice(0, maxBanners) : banners;

  // Calculate responsive height based on aspect ratio if provided
  const getResponsiveHeight = () => {
    if (aspectRatio) {
      return `clamp(${minHeight}, ${100 / aspectRatio}vw, ${maxHeight})`;
    }
    return height;
  };

  if (loading && showLoadingImage) {
    return (
      <div
        className={`banner-loading ${className}`}
        style={{
          width: '100%',
          maxWidth: width,
          margin: '0 auto',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            width: '100%',
            height: getResponsiveHeight(),
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <img
            src={loadingImage}
            alt="Loading banners..."
            style={{
              width: '100%',
              height: '100%',
              objectFit: imageFit,
              objectPosition: 'center',
              display: 'block'
            }}
          />

        </div>
      </div>
    );
  }

  if (error) return null;
  if (!displayBanners.length) return null;

  const getSectionClass = (section) => {
    const sectionClasses = {
      hero: 'banner-hero',
      featured: 'banner-featured',
      middle: 'banner-middle',
      bottom: 'banner-bottom',
      sidebar: 'banner-sidebar',
      top: 'banner-top',
      widget: 'banner-widget',
      notification: 'banner-notification',
      between_products: 'banner-between-products',
      above_product: 'banner-above-product',
      below_product: 'banner-below-product',
      related_products: 'banner-related-products',
      payment_section: 'banner-payment',
      header: 'banner-header',
      footer: 'banner-footer',
      floating: 'banner-floating',
      popup: 'banner-popup'
    };
    return sectionClasses[section] || 'banner-default';
  };

  const getLayoutClass = (layout) => {
    const layoutClasses = {
      default: 'banner-layout-default',
      slider: 'banner-layout-slider',
      grid: 'banner-layout-grid',
      stack: 'banner-layout-stack'
    };
    return layoutClasses[layout] || 'banner-layout-default';
  };

  return (
    <div
      className={`banner-container ${getSectionClass(section)} ${getLayoutClass(layout)} ${className}`}
      style={{
        width: '100%',
        maxWidth: width,
        margin: '0 auto',
        overflow: 'hidden'
      }}
    >
      {layout === 'slider' ? (
        <BannerSlider
          banners={displayBanners}
          onBannerClick={handleBannerClick}
          height={getResponsiveHeight()}
          width="100%"
          aspectRatio={aspectRatio}
          minHeight={minHeight}
          maxHeight={maxHeight}
          imageFit={imageFit}
        />
      ) : layout === 'grid' ? (
        <BannerGrid
          banners={displayBanners}
          onBannerClick={handleBannerClick}
          aspectRatio={aspectRatio}
          minHeight={minHeight}
          maxHeight={maxHeight}
          imageFit={imageFit}
        />
      ) : (
        <BannerStack
          banners={displayBanners}
          onBannerClick={handleBannerClick}
          height={getResponsiveHeight()}
          width="100%"
          aspectRatio={aspectRatio}
          minHeight={minHeight}
          maxHeight={maxHeight}
          imageFit={imageFit}
        />
      )}
    </div>
  );
};

const BannerSlider = ({ banners, onBannerClick, height, width, aspectRatio, minHeight, maxHeight, imageFit }) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);

  React.useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  return (
    <div
      className="banner-slider"
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: height,
        borderRadius: '8px' // Added for better visual appeal
      }}
    >
      <div
        className="banner-slides"
        style={{
          display: 'flex',
          transition: 'transform 0.5s ease-in-out',
          transform: `translateX(-${(currentSlide * 100) / banners.length}%)`,
          width: `${banners.length * 100}%`,
          height: '100%'
        }}
      >
        {banners.map((banner, index) => (
          <div
            key={banner._id}
            className="banner-slide"
            style={{
              flex: `0 0 ${100 / banners.length}%`,
              width: `${100 / banners.length}%`,
              height: '100%',
              position: 'relative'
            }}
          >
            <BannerItem
              banner={banner}
              onClick={onBannerClick}
              index={index}
              width="100%"
              height="100%"
              aspectRatio={aspectRatio}
              minHeight={minHeight}
              maxHeight={maxHeight}
              imageFit={imageFit}
            />
          </div>
        ))}
      </div>

      {/* Navigation dots */}
      {banners.length > 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '8px',
            zIndex: 10
          }}
        >
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: index === currentSlide ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease'
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BannerStack = ({ banners, onBannerClick, height, width, aspectRatio, minHeight, maxHeight, imageFit }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
    {banners.map((banner, index) => (
      <BannerItem
        key={banner._id}
        banner={banner}
        onClick={onBannerClick}
        index={index}
        height={height}
        width={width}
        aspectRatio={aspectRatio}
        minHeight={minHeight}
        maxHeight={maxHeight}
        imageFit={imageFit}
      />
    ))}
  </div>
);

const BannerGrid = ({ banners, onBannerClick, aspectRatio, minHeight, maxHeight, imageFit }) => (
  <div
    className="banner-grid"
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1rem',
      width: '100%'
    }}
  >
    {banners.map((banner, index) => (
      <div key={banner._id} className="banner-grid-item">
        <BannerItem
          banner={banner}
          onClick={onBannerClick}
          index={index}
          width="100%"
          height={aspectRatio ? `clamp(${minHeight}, ${100 / aspectRatio}vw, ${maxHeight})` : '300px'}
          aspectRatio={aspectRatio}
          minHeight={minHeight}
          maxHeight={maxHeight}
          imageFit={imageFit}
        />
      </div>
    ))}
  </div>
);

const BannerItem = ({ banner, onClick, index, height = '400px', width = '100%', aspectRatio, minHeight, maxHeight, imageFit = 'cover' }) => {
  const [isVisible, setIsVisible] = React.useState(true);
  if (!isVisible) return null;

  const handleClick = () => onClick?.(banner);
  const handleClose = (e) => {
    e.stopPropagation();
    setIsVisible(false);
  };

  return (
    <div
      className={`banner-item priority-${banner.priority}`}
      style={{
        width: width,
        height: height,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        display: 'block'
      }}
      onClick={handleClick}
    >
      {banner.image?.url && (
        <img
          src={getImageUrl(banner.image.url)}
          alt={banner.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: imageFit,
            objectPosition: 'center',
            transition: 'transform 0.3s ease',
            display: 'block',
            backgroundColor: '#f0f0f0',
            position: 'absolute',
            top: 0,
            left: 0
          }}
          onMouseEnter={(e) => imageFit === 'cover' && (e.target.style.transform = 'scale(1.05)')}
          onMouseLeave={(e) => imageFit === 'cover' && (e.target.style.transform = 'scale(1)')}
        />
      )}

      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: imageFit === 'cover',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#fff',
          textAlign: 'center',
          padding: 'clamp(0.5rem, 4vw, 2rem)',
          zIndex: 2
        }}
      >
        <h3
          style={{
            fontSize: 'clamp(1.2rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
          }}
        >
          {banner.title}
        </h3>
        <p
          style={{
            fontSize: 'clamp(0.9rem, 2.5vw, 1.2rem)',
            marginBottom: '1rem',
            maxWidth: '80%',
            textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
          }}
        >
          {banner.subtitle}
        </p>
        {banner.ctaText && (
          <button
            className="btn btn-primary"
            style={{
              padding: 'clamp(0.5rem, 2vw, 1rem) clamp(1rem, 4vw, 2rem)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
              fontWeight: 'bold',
              borderRadius: '25px',
              border: 'none',
              backgroundColor: '#007bff',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#0056b3';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#007bff';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {banner.ctaText}
          </button>
        )}
      </div>
    </div>
  );
};

export default BannerDisplay;
